/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import padStart from 'lodash/padStart';
import { updateSearchCriterias } from 'ui-search/src/actions/search';
import TripPlannerMap from 'shared-ui/maps/templates/TripPlannerMap';
import {
    tripPlannerSources,
    MAP_LAYER_CONTROL_CONTENT,
    DESTINATION_PIN,
    DESTINATION_START_PIN,
} from 'shared-ui/maps/constants';
import { featureCollection, point } from 'shared-ui/maps/turfUtils';
import { getInventoryItemDetails } from '../actions/tripPlanner';
import {
    getItemDescription,
    isTripEditable,
    isTripItemFeatureInItinerary,
} from '../utils/tripUtils';
import {
    convertSearchResultsToGEOJson,
    convertTripItemsToGeojson,
} from '../utils/mapUtils';

function MapContainer(props) {
    const dispatch = useDispatch();

    const trip = useSelector((state) => get(state, 'tripPlanner.trip'));
    const tripDestinations = useSelector((state) =>
        get(state, 'tripPlanner.trip.destinations')
    );
    const searchLocation = useSelector((state) =>
        get(state, 'tripPlanner.searchLocation')
    );
    const searchLocationLat = useSelector((state) =>
        get(state, 'tripPlanner.searchLocation.lat')
    );
    const searchLocationLng = useSelector((state) =>
        get(state, 'tripPlanner.searchLocation.lng')
    );
    const itemDetails = useSelector((state) =>
        get(state, 'tripPlannerSearchResults.inventoryItemDetails.results', null)
    );
    const activeItemIndex = useSelector((state) =>
        get(state, 'tripPlanner.activeItemIndex')
    );
    const activeDestinationIndex = useSelector((state) =>
        get(state, 'tripPlanner.activeDestinationIndex')
    );
    const tripFilters = useSelector((state) => get(state, 'tripPlanner.fg'));

    const geojsons = useRef({
        lineRoute: { sourceName: tripPlannerSources.lineRoute, isClustered: false },
        polygonRoute: { sourceName: tripPlannerSources.polygonRoute, isClustered: false },
        recreation: { sourceName: tripPlannerSources.recreation, isClustered: false },
        tripDestinations: {
            sourceName: tripPlannerSources.tripDestinations,
            isClustered: false,
        },
        tripItems: { sourceName: tripPlannerSources.tripItems, isClustered: false },
    });

    const config = useRef({
        mapSpecialControls: {
            legendContent: <ul />,
            layerContent: [
                ['recreationLayer', 'recreationCircleLayer'],
                MAP_LAYER_CONTROL_CONTENT,
            ],
            ignoreLayer: !props.mapIsMapLayerControlVisible,
        },
        disableMapScroll: !props.mapIsZoomByScrollEnabled,
    });
    const itemDetailsRef = useRef(null);
    const isLocalSelection = useRef(false);
    const shouldUpdateAsset = useRef(false);

    const [lineRouteData, setLineRouteData] = useState(featureCollection([]));
    const [polygonRouteData, setPolygonRouteData] = useState(featureCollection([]));
    const [tripDestinationData, setTripDestinationData] = useState(featureCollection([]));
    const [tripItemsData, setTripItemsData] = useState(featureCollection([]));
    const [recreationData, setRecreationData] = useState(featureCollection([]));
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [center, setCenter] = useState([-106, 40]);
    const [zoom, setZoom] = useState(4);
    const [destinationIndex, setDestinationIndex] = useState(activeDestinationIndex);
    const [itemIndex, setItemIndex] = useState(activeItemIndex);
    const [isItemAdded, setIsItemAdded] = useState(false);

    const getSearchInventoryItemDetails = (params) =>
        dispatch(getInventoryItemDetails(params));

    const convertTripDestinationsToGeojson = (destinations = []) => {
        const tripDestinationGeoJson = featureCollection([]);

        tripDestinationGeoJson.features = destinations.map((destination, i) => {
            let icon = DESTINATION_PIN;
            if (i === 0) icon = DESTINATION_START_PIN;
            else if (i < 20) {
                const pinNumber =
                    i < 9
                        ? padStart((i + 1).toString(), 2, '0') // 01-09
                        : `0${i + 1}`;
                icon = `${DESTINATION_PIN}-${pinNumber}`;
            }
            const destinationProps = {
                name: destination.name,
                description: destination.name,
                featureId: destination.name,
                entityId: destination.entity_id,
                entityType: destination.entity_type,
                imageUrl: destination.preview_image_url,
                shortDescription: getItemDescription(destination),
                isStartLocation: i === 0,
                icon,
            };

            const destinationPoint = point(
                [destination.lng, destination.lat],
                destinationProps
            );
            return destinationPoint;
        });

        return tripDestinationGeoJson;
    };

    const buildLocation = (feature) => {
        if (!feature) return {};
        return {
            name: feature.name,
            description: feature.name,
            featureId: feature.name,
            entityId: feature.entity_id,
            entityType: feature.entity_type,
            imageUrl: feature.preview_image_url,
            shortDescription: getItemDescription(feature),
        };
    };

    useEffect(() => {
        setItemIndex(activeItemIndex);
    }, [activeItemIndex]);

    useEffect(() => {
        setDestinationIndex(isItemAdded ? null : activeDestinationIndex);
    }, [activeDestinationIndex, isItemAdded]);

    useEffect(() => {
        if ((destinationIndex === null && itemIndex === null) || isItemAdded) {
            setIsItemAdded(false);
            return;
        }

        const destination = tripDestinations[destinationIndex];
        if (!destination) return;
        const isItem = itemIndex !== null;

        let item = null;
        if (isItem) item = destination.items[itemIndex];

        const locationProps = buildLocation(isItem ? item : destination);
        locationProps.isSearchDestination = !isItem;
        const featCenter = isItem
            ? [item?.lng, item?.lat]
            : [destination?.lng, destination?.lat];
        if (!featCenter[0]) return;

        setCenter(featCenter);
        if (zoom !== 8) setZoom(8);

        const featPoint = point(featCenter, locationProps);
        if (!isEqual(featPoint, selectedFeature)) {
            isLocalSelection.current = true;
            setSelectedFeature(featPoint);
        }
    }, [itemIndex, destinationIndex]);

    useEffect(() => {
        if (!props.routeLine || !props.routePolygon || !tripDestinations) return;

        const destinations = convertTripDestinationsToGeojson(tripDestinations);
        if (
            destinations?.features?.length &&
            !isEqual(tripDestinationData, destinations)
        ) {
            setTripDestinationData(destinations);
            setSelectedFeature(null);
        }

        const tripItems = convertTripItemsToGeojson(tripDestinations);
        if (tripItems.features?.length && !isEqual(tripItemsData, tripItems)) {
            setTripItemsData(tripItems);
            setSelectedFeature(null);
        }
    }, [props.routeLine, props.routePolygon, tripDestinations]);

    useEffect(() => {
        const locationCenter =
            searchLocationLng && searchLocationLat
                ? [searchLocationLng, searchLocationLat]
                : null;
        if (locationCenter && !isEqual(center, locationCenter)) {
            setCenter(locationCenter);

            const locationProps = {
                name: searchLocation.name,
                description: searchLocation.name,
                featureId: searchLocation.name,
                entityId: searchLocation.entity_id,
                entityType: searchLocation.entity_type,
                imageUrl: searchLocation.preview_image_url,
                shortDescription: getItemDescription(searchLocation),
                icon: DESTINATION_PIN,
                isStartLocation: false,
                isSearchDestination: true,
            };

            const searchDestination = point(locationCenter, locationProps);
            if (!isEqual(searchDestination, selectedFeature)) {
                isLocalSelection.current = true;
                setSelectedFeature(searchDestination);
            }
        }
        if (zoom !== 8) setZoom(8);
    }, [searchLocationLat, searchLocationLng]);

    useEffect(() => {
        const isDestinationIndex = destinationIndex !== null && itemIndex === null;

        if (
            !selectedFeature?.properties ||
            selectedFeature.asset ||
            (isDestinationIndex && isLocalSelection.current)
        ) {
            isLocalSelection.current = false;
            return;
        }
        shouldUpdateAsset.current = true;

        const { name, type, entityId, entityType } = selectedFeature.properties;
        const searchParams = {
            entity_id: entityId,
            entity_type: entityType,
            name,
            type,
        };
        getSearchInventoryItemDetails(searchParams);
    }, [selectedFeature]);

    const onClickAddToItineraryButton = (e) => {
        const item = {
            entity_id: e.entityId,
            entity_type: e.entityType,
            lat: e.lat,
            lng: e.lng,
            name: e.name,
            reservable: e.reservable,
            availability: e.availability,
            availability_counts: e.availability_counts,
            campsites_count: e.campsitesCount,
            average_rating: e.average_rating,
            number_of_ratings: e.number_of_ratings,
            tours_count: e.toursCount,
            org_id: e.org_id,
            org_name: e.org_name,
            parent_id: e.parent_id,
            parent_name: e.parent_name,
            parent_type_name: e.parent_type_name,
            campsite_type_of_use: e.campsite_type_of_use,
            activities: e.activities,
            city: e.city,
            state_code: e.state_code,
            country_code: e.country_code,
            description: e.description,
            type: e.type,
            preview_image_url: e.imageUrl,
        };

        setIsItemAdded(true);
        // add item to currently active destination in itinerary
        if (props.onAddTripItem) props.onAddTripItem(item);
    };

    const onClickDeleteFromItineraryButton = (e) => {
        const item = {
            entity_id: e.entityId,
            entity_type: e.entityType,
            lat: e.lat,
            lng: e.lng,
            name: e.title,
        };

        // remove item from trip entirely
        if (props.onDeleteTripItem) props.onDeleteTripItem(item);
    };

    const onClickDetailsButton = (asset) => {
        dispatch(
            updateSearchCriterias({
                fg: tripFilters,
            })
        );

        window.open(asset.targetUri, '_blank', 'noopener,noreferrer');
    };

    useEffect(() => {
        const isDestinationIndex = destinationIndex !== null && itemIndex === null;
        const hasItemsChanged = isEqual(itemDetailsRef.current, itemDetails);

        if (
            !itemDetails?.length ||
            itemDetails[0].entity_id !== selectedFeature?.properties?.entityId ||
            (hasItemsChanged && selectedFeature?.asset) ||
            (isDestinationIndex && !shouldUpdateAsset.current)
        ) {
            shouldUpdateAsset.current = false;
            return;
        }

        itemDetailsRef.current = itemDetails;
        const itemsGeojson = convertSearchResultsToGEOJson(itemDetails);
        const itemProps = itemsGeojson.features[0]?.properties;
        const itemGeom = itemsGeojson.features[0]?.geometry;

        if (!itemProps || !itemGeom || !selectedFeature?.geometry) return;

        const asset = {
            imageUrl: itemProps.imageUrl,
            imageTitle: itemProps.imageTitle,
            name: itemProps.name,
            title: itemProps.title,
            location: itemProps.location,
            description: itemProps.shortDescription,
            reservable: itemProps.reservable,
            targetUri: itemProps.targetUri,
            entityId: itemProps.entityId,
            entityType: itemProps.entityType,
            entity_type: itemProps.entity_type,
            recAreaName: itemProps.recAreaName,
            lat: itemGeom.coordinates[1],
            lng: itemGeom.coordinates[0],
            rate: itemProps.rate,
            campsitesCount: itemProps.campsitesCount,
            average_rating: itemProps.average_rating,
            number_of_ratings: itemProps.number_of_ratings,
            toursCount: itemProps.toursCount,
            availability: itemProps.availability,
            availability_counts: itemProps.availability_counts,
            org_id: itemProps.org_id,
            org_name: itemProps.org_name,
            parent_id: itemProps.parent_id,
            parent_name: itemProps.parent_name,
            parent_type_name: itemProps.parent_type_name,
            campsite_type_of_use: itemProps.campsite_type_of_use,
            activities: itemProps.activities,
            city: itemProps.city,
            state_code: itemProps.state_code,
            country_code: itemProps.country_code,
            type: itemProps.type,
        };

        asset.isInItinerary = isTripItemFeatureInItinerary(
            itemsGeojson.features[0],
            tripDestinations
        );
        asset.isTripEditable = isTripEditable(trip);
        asset.onClick = asset.isInItinerary
            ? onClickDeleteFromItineraryButton
            : onClickAddToItineraryButton;
        asset.onDetailsClick = onClickDetailsButton;

        const updatedFeature = {
            ...selectedFeature,
            asset,
            geometry: selectedFeature.geometry,
        };

        if (!isEqual(selectedFeature, updatedFeature)) {
            isLocalSelection.current = true;
            setSelectedFeature(updatedFeature);
        }
    }, [itemDetails]);

    useEffect(() => {
        if (props.mapZoom && !isEqual(zoom, props.mapZoom)) setZoom(props.mapZoom);
    }, [props.mapZoom]);

    useEffect(() => {
        if (!props.routeLine) return;
        if (props.routePolygon) {
            const line = featureCollection([props.routeLine]);
            if (!isEqual(lineRouteData, line)) setLineRouteData(line);

            const polygon = featureCollection([props.routePolygon]);
            if (!isEqual(polygonRouteData, polygon)) setPolygonRouteData(polygon);
        }
    }, [props.routeLine, props.routePolygon]);

    useEffect(() => {
        if (props.mapGeoJsonData && !isEqual(recreationData, props.mapGeoJsonData)) {
            setRecreationData(props.mapGeoJsonData);
        }
    }, [props.mapGeoJsonData]);

    return (
        <TripPlannerMap
            geojsons={geojsons.current}
            config={config.current}
            lineRoute={lineRouteData}
            polygonRoute={polygonRouteData}
            recreation={recreationData}
            tripDestination={tripDestinationData}
            tripItems={tripItemsData}
            service="tripplanner"
            center={center}
            zoom={zoom}
            mapContainer={props.mapContainer}
            setSelectedFeature={setSelectedFeature}
            selectedFeature={selectedFeature}
            onMapMoveEnd={props.onMapMoveEnd}
        />
    );
}

MapContainer.propTypes = {
    mapContainer: PropTypes.string,
    mapIsZoomByScrollEnabled: PropTypes.bool,
    mapIsMapLayerControlVisible: PropTypes.bool,
    mapZoom: PropTypes.number,
    routeLine: PropTypes.object,
    routePolygon: PropTypes.object,
    mapGeoJsonData: PropTypes.object,
    onMapMoveEnd: PropTypes.func,
    onAddTripItem: PropTypes.func,
    onDeleteTripItem: PropTypes.func,
};

export default MapContainer;
